import React, { useEffect, useState } from "react";
import CompanyHeader from "./CompanyHeader";
import Footer from "../Home/Footer";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import AxiosInstance from "../Home/AxiosInstance";
import "./MultipleProfiles.css";
import "./employerPortal.css";
import profile from "../Home/Profile.png";

export default function UserProfilePage() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const location = useLocation();
  const [userDetails, setUserDetails] = useState(null);
  const [userProfiles, setUserProfiles] = useState([]);
  const [roles, setRoles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch user details when the component mounts
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userDetailsResponse = await AxiosInstance.get(
          `companies/user/details/${userId}/`
        );
        setUserDetails(userDetailsResponse.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching user details:", error);
        setLoading(false);
      }
    };

    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  // Fetch available roles for adding profiles
  const fetchRoles = async () => {
    try {
      const response = await AxiosInstance.get(
        "companies/roles/"
      );
      setRoles(response.data); // Set the fetched roles
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  // Fetch profiles associated with the user
  const fetchProfiles = async () => {
    try {
      const profilesResponse = await AxiosInstance.get(
        `companies/user/profile/${userId}/`
      );
      setUserProfiles(profilesResponse.data); // Set the fetched profiles
    } catch (error) {
      console.error("Error fetching user profiles:", error);
    }
  };

  // Fetch roles when the component mounts
  useEffect(() => {
    fetchRoles();
    fetchProfiles();
  }, []);

  // Reset the form fields
  const resetForm = () => {
    setProfileName("");
    setEmail("");
    setRole("");
    setError("");
  };

  // Handle the addition of a profile
  const handleProfileAdded = (newProfile) => {
    setUserProfiles((prevProfiles) => [...prevProfiles, newProfile]); // Add the new profile to the list
  };

  // Handle form submission for adding a profile
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Structure the profile data correctly
      const profileData = {
        role: role,
        user: userId,
        profile_name: profileName,
        email: email,
      };

      // Call the API to add the profile
      const response = await AxiosInstance.post(
        `companies/profile/`,
        profileData
      );

      // On success, fetch the updated profiles
      fetchProfiles(); // Fetch the updated profiles list after adding a new one

      setIsModalOpen(false); // Close the modal
      resetForm(); // Reset the form fields
    } catch (error) {
      console.error("Error adding profile:", error);
      alert("Failed to add profile.");
    }
  };

  const handleBack = () => {
    const from = location.state?.from;
    if (from) {
      navigate(from);
      return;
    }
    const companyId = localStorage.getItem("company_id");
    const isInstitution = location.pathname.includes("institution");
    if (companyId) {
      navigate(
        isInstitution
          ? `/institution-profile/${companyId}`
          : `/company-profile/${companyId}`
      );
      return;
    }
    navigate(isInstitution ? "/institution-details" : "/company-details");
  };

  // Handle navigating to the profile page
  const handleProfileClick = (profileId) => {
    navigate(`/profiles/${profileId}`, {
      state: { from: location.pathname },
    });
  };

  return (
    <div className="CompanyPage als-employer-page">
      <CompanyHeader />
      <div className="company-container py-4">
        <div className="mb-3">
          <button
            className="navigate-button btn btn-outline-primary d-flex align-items-center"
            onClick={handleBack}
            style={{ gap: "6px" }}
          >
            <FiArrowLeft /> Back
          </button>
        </div>
        <h3 className="section-heading">Employee Details:</h3>
        {/* Render loading if userDetails is not available */}
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="card-custom d-flex align-items-start gap-4 flex-wrap">
            <img
              src={
                userDetails.image_path
                  ? `${AxiosInstance.defaults.baseURL}/media/${userDetails.image_path}`
                  : profile
              }
              alt="Profile"
              className="profile-image"
            />
            <div className="flex-grow-1">
              <p>
                <strong>Name:</strong> {userDetails.first_name}{" "}
                {userDetails.last_name}
              </p>
              <p>
                <strong>Email:</strong> {userDetails.email}
              </p>
              <p>
                <strong>Mobile Number:</strong> {userDetails.mobile_no}
              </p>
              <p>
                <strong>Gender:</strong> {userDetails.gender}
              </p>
            </div>
          </div>
        )}
        {/* Display profiles section */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="section-heading">Profiles:</h3>
          <button
            className="btn btn-primary"
            onClick={() => {
              setIsModalOpen(true);
              resetForm();
            }}
          >
            Add Profile
          </button>
        </div>
        <div className="row">
          {userProfiles.length > 0 ? (
            userProfiles.map((profile, index) => (
              <div key={index} className="col-md-4 mb-4">
                <div
                  className="card h-100 shadow-sm"
                  onClick={() => handleProfileClick(profile.profile_id)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="card-body">
                    <h5 className="card-title">{profile.profile_name}</h5>
                    <p className="card-text">
                      <strong>Email:</strong> {profile.email}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p>No profiles assigned</p>
          )}
        </div>
      </div>

      {/* Add Profile Modal */}
      {isModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add Profile</h5>
              </div>
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Profile Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      required
                    >
                      <option value="">Select Role</option>
                      {roles.map((roleItem) => (
                        <option key={roleItem.role_id} value={roleItem.role_id}>
                          {roleItem.role}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Save Profile
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
